import React, { useEffect, useState } from "react";
import { Modal, ModalBody, CardFooter, Button } from "reactstrap";
import { useQueryClient, useMutation } from "react-query";
import { injectIntl } from "react-intl";
import { toast } from "react-toastify";
import { FormattedMessage } from "react-intl";
import ConfirmDialog from "../Modal/ConfirmDialog";
import {
  GetAvailableStudentsForPeriod,
  GetProgramPeriods,
} from "../../actions/teacherQueries";
import { ChangeStudentsAndSupervisorsInPeriod } from "../../actions/teacherMutations";
import CreateAPLPeriodSupervisor from "./CreateAPLPeriodSupervisor";

const ModifySupervisorsModal = ({
  allSupervisors,
  allStudents,
  isOpen,
  toggle,
  period,
  listOfStudentIds,
  schoolId,
  level,
  objId,
  programTeachers,
  programId,
  teacherInfo,
}) => {
  const [newStudentsAndSupervisors, setNewStudentsAndSupervisors] = useState(
    []
  );
  const [selectableStudents, setSelectableStudents] = useState([]);
  const [promptRemoveStudents, setPromptRemoveStudents] = useState(false);
  const [studentsForRemoval, setStudentsForRemoval] = useState([]);
  const [allStudentsInPeriod, setAllStudentsInPeriod] = useState([]);
  const [studentsNotInLevelNotToChange, setStudentsNotInLevelNotToChange] =
    useState([]);
  const [postButtonDisabled, setPostButtonDisabled] = useState(true);
  const allPeriods = GetProgramPeriods(programId);

  const mutateChangeStudentsAndSupervisorsInPeriod = useMutation(
    ChangeStudentsAndSupervisorsInPeriod
  );

  const availableStudentIds = GetAvailableStudentsForPeriod({
    candidates: {
      periodId: period.period.id,
      candidateStudentIds: listOfStudentIds,
    },
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!allPeriods.isLoading && period?.period) {
      const tempAllPeriodStudents = [];
      const currentPeriod = allPeriods.data.find(
        (p) => p.period.id === period.period.id
      );
      currentPeriod.period?.students?.forEach((stud) => {
        const studentToAdd = {
          ...stud.student,
          companyId: stud.companyId,
          studentId: stud.student.id,
          supervisors: [],
        };
        stud.supervisors.forEach((studsup) => {
          const supervisorToAdd = {
            id: studsup.supervisor.id,
            aplCompany: { ...studsup.supervisor.aplCompany },
            name: studsup.supervisor.name,
            email: studsup.supervisor.email,
            userType: studsup.supervisor.userType,
            userTypeOf: studsup.supervisor.userTypeOf,
            activatedAccount: studsup.supervisor,
            deleted: studsup.supervisor.deleted,
            created: studsup.supervisor.created,
            isMainResponsible: studsup.isMainResponsible,
          };
          studentToAdd.supervisors.push(supervisorToAdd);
        });
        tempAllPeriodStudents.push(studentToAdd);
      });
      setAllStudentsInPeriod(tempAllPeriodStudents);
    }
  }, [allPeriods.data, allPeriods.isLoading, period]);

  useEffect(() => {
    const tempStudentsNotInLevelNotToChange = [];
    allStudentsInPeriod.forEach((stud) => {
      if (
        !period.period.students.some(
          (currstud) => currstud.student.id === stud.id
        )
      ) {
        tempStudentsNotInLevelNotToChange.push(stud);
      }
    });
    setStudentsNotInLevelNotToChange(tempStudentsNotInLevelNotToChange);
    setPostButtonDisabled(false);
  }, [allStudentsInPeriod, period.period.students]);

  useEffect(() => {
    if (
      period?.period &&
      availableStudentIds.data &&
      !availableStudentIds.isLoading &&
      !availableStudentIds.error
    ) {
      const tempNewStudentsAndSupervisors = [];
      period.period.students.forEach((stud) => {
        const studentToAdd = {
          ...stud.student,
          companyId: stud.companyId,
          studentId: stud.student.id,
          supervisors: [],
        };
        stud.supervisors.forEach((studsup) => {
          const supervisorToAdd = {
            id: studsup.supervisor.id,
            aplCompany: { ...studsup.supervisor.aplCompany },
            name: studsup.supervisor.name,
            email: studsup.supervisor.email,
            userType: studsup.supervisor.userType,
            userTypeOf: studsup.supervisor.userTypeOf,
            activatedAccount: studsup.supervisor,
            deleted: studsup.supervisor.deleted,
            created: studsup.supervisor.created,
            isMainResponsible: studsup.isMainResponsible,
          };
          studentToAdd.supervisors.push(supervisorToAdd);
        });
        tempNewStudentsAndSupervisors.push(studentToAdd);
      });
      setNewStudentsAndSupervisors(tempNewStudentsAndSupervisors);
      let tempAvailableStudents = [];
      const availableStudentsWithCurrentStudents = [
        ...availableStudentIds.data,
      ];
      tempNewStudentsAndSupervisors.forEach((stud) => {
        availableStudentsWithCurrentStudents.push(stud.id);
      });
      tempAvailableStudents = allStudents.filter((stud) =>
        availableStudentsWithCurrentStudents.includes(stud.id)
      );

      let tempStudentsForSorting = [];
      tempAvailableStudents.forEach((tempstud) => {
        if (
          !tempStudentsForSorting.some(
            (stud) => stud.id === tempstud.student.id
          )
        ) {
          let studentToAdd = { ...tempstud.student };
          tempStudentsForSorting.push(studentToAdd);
        }
      });

      setSelectableStudents(tempStudentsForSorting);
    }
  }, [
    period,
    availableStudentIds.isLoading,
    availableStudentIds.error,
    availableStudentIds.data,
    allStudents,
  ]);

  const saveChanges = () => {
    const allStudentsIncluded = period.period.students.every((oldStud) =>
      newStudentsAndSupervisors.some(
        (currStud) => currStud.id === oldStud.studentId
      )
    );
    if (allStudentsIncluded) {
      handlePostChanges();
    } else {
      const tempStudentsForRemoval = [];
      period.period.students.forEach((oldStud) => {
        if (
          !newStudentsAndSupervisors.some(
            (currStud) => currStud.id === oldStud.studentId
          )
        ) {
          tempStudentsForRemoval.push(oldStud);
        }
      });
      setStudentsForRemoval(tempStudentsForRemoval);
      setPromptRemoveStudents(true);
    }
  };

  const handlePostChanges = () => {
    setStudentsForRemoval([]);
    setPromptRemoveStudents(false);
    setPostButtonDisabled(true);
    const studentsToPost = [];
    newStudentsAndSupervisors.forEach((stud) => {
      let studentToAdd = {
        name: stud.name,
        companyId: stud.companyId,
        studentId: stud.id,
        supervisors: stud.supervisors,
      };
      if (studentToAdd.supervisors.length === 0) {
        studentToAdd.supervisors.push({
          ...teacherInfo.data,
          IsMainResponsible: true,
        });
      }
      studentsToPost.push(studentToAdd);
    });
    studentsNotInLevelNotToChange.forEach((stud) => studentsToPost.push(stud));

    mutateChangeStudentsAndSupervisorsInPeriod.mutate(
      {
        periodId: period.period.id,
        deleteMissing: true,
        students: studentsToPost,
      },
      {
        onSuccess: (data) => {
          if (data && data.status > 199 && data.status < 300) {
            setPostButtonDisabled(false);
            toast.success(<FormattedMessage id="PeriodUpdated" />);
            handleReloadSupervisors();
            toggle();
          } else {
            toast.warn(data);
          }
        },
        throwOnError: true,
        onError: (data) => {
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleReloadSupervisors = () => {
    queryClient.invalidateQueries(["FetchProgramPeriods", [programId]]);
    queryClient.invalidateQueries(["FetchPeriods", [level, objId]]);
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="xl">
        <ModalBody>
          <CreateAPLPeriodSupervisor
            students={selectableStudents}
            supervisors={allSupervisors}
            aplStudentSupervisors={newStudentsAndSupervisors}
            setAplStudentSupervisors={setNewStudentsAndSupervisors}
            schoolId={schoolId}
            programTeachers={programTeachers}
            isModifying={true}
            isOpen={isOpen}
          />
        </ModalBody>
        <CardFooter>
          <div className="justify-content-end d-flex gap-2 m-3">
            <Button outline color="secondary" onClick={() => toggle()}>
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              outline
              color="primary"
              onClick={() => saveChanges()}
              disabled={postButtonDisabled}
            >
              <FormattedMessage id="Save" />
            </Button>
          </div>
        </CardFooter>
      </Modal>
      <ConfirmDialog
        isOpen={promptRemoveStudents}
        toggle={() => setPromptRemoveStudents(!promptRemoveStudents)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          <FormattedMessage
            values={{
              students: studentsForRemoval.map((a) => " " + a.student.name),
            }}
            id="WillBeRemovedFromPeriod"
          />
        }
        onProceed={handlePostChanges}
        danger={true}
      />
    </>
  );
};
export default injectIntl(ModifySupervisorsModal);
